import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { AppDispatch, RootState } from 'app/store';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from 'Services/APIService';
import {
  clearCategoryError,
  clearSubcategoryError,
  clearTemplateError,
  crudCategories,
  getTemplateById
} from 'slices/payrolslices/documentTemplateSlice';
import DocumentEditingStep from './create/DocumentEditingStep';
import DocumentUploadStep from './create/DocumentUploadStep';
import TemplateConfigStep from './create/TemplateConfigStep';
import { useDocumentManagement } from './DocumentManagementContext';
import { FieldDataType, SignatureField, UploadedFile } from './types';

interface CreateDocumentPageProps {
  documentId?: string;
  documentName?: string;
  initialSigners?: string[];
}

// Custom hook to prevent useEffect from running on initial mount
const useDidUpdateEffect = (effect: () => void, deps: React.DependencyList) => {
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) {
      return effect();
    } else {
      didMount.current = true;
    }
  }, deps);
};

const CreateDocumentPage: React.FC<CreateDocumentPageProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { setActiveView } = useDocumentManagement();
  const { documentId, documentName, selectedCategory, template } = location.state || {};
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Redux state
  const {
    availableCategories,
    categoryLoading,
    categoryError,
    subcategoryError,
    templateCreating,
    error: templateError,
    success: templateSuccess,
    selectedTemplate,
    templateFetching
  } = useSelector((state: RootState) => state.documentTemplate);

  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );

  console.log('template for edit:', template);

  // Edit mode detection
  const isEditMode = !!documentId;

  // Ref to prevent multiple fetches
  const hasFetchedTemplate = useRef(false);
  const templateInitialized = useRef(false);

  // NEW: Track if user has actually submitted the form
  const hasSubmitted = useRef(false);

  // Main state
  const [step, setStep] = useState(isEditMode ? 2 : 1);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [documentNameState, setDocumentNameState] = useState(documentName || '');
  const [category, setCategory] = useState(selectedCategory || '');
  const [subCategory, setSubCategory] = useState<string>('');
  const [signatureFields, setSignatureFields] = useState<SignatureField[]>([]);
  const [fieldRequirements, setFieldRequirements] = useState<Record<string, boolean>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState<string | null>(null);

  // Document preview function
  const fetchDocumentPreview = async (storedFilePath: string) => {
    try {
      setPreviewLoading(true);
      const encodedPath = encodeURIComponent(storedFilePath);
      const response = await axiosInstance.get(`/avatar/get-avatar?path=${encodedPath}`, {
        responseType: 'blob'
      });

      const blobUrl = URL.createObjectURL(response.data);
      setDocumentPreviewUrl(blobUrl);
      return blobUrl;
    } catch (error) {
      console.error('Error fetching document preview:', error);
      setErrorMessage('Failed to load document preview.');
      return null;
    } finally {
      setPreviewLoading(false);
    }
  };

  // Enhanced resetToStepOne function
  const resetToStepOne = useCallback(() => {
    setDocumentNameState('');
    setUploadedFile(null);
    setCategory('');
    setSubCategory('');
    setSignatureFields([]);
    setStep(1);
    setFieldRequirements({});
    setSuccessMessage(null);
    setErrorMessage(null);
    setDocumentPreviewUrl(null);
    hasFetchedTemplate.current = false;
    templateInitialized.current = false;
    hasSubmitted.current = false; // Reset submission flag
    dispatch(clearTemplateError());
    dispatch(clearCategoryError());
    dispatch(clearSubcategoryError());
  }, [dispatch]);

  // Load categories on component mount
  useEffect(() => {
    if (selectedProjectId && selectedServiceTitle) {
      dispatch(
        crudCategories({
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          action: 'get'
        })
      );
    }
  }, [dispatch, selectedProjectId, selectedServiceTitle]);

  // Fetch template data for edit mode - FIXED to prevent infinite loop
  useEffect(() => {
    if (
      isEditMode &&
      documentId &&
      selectedProjectId &&
      selectedServiceTitle &&
      !hasFetchedTemplate.current
    ) {
      hasFetchedTemplate.current = true;
      dispatch(
        getTemplateById({
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          document_id: documentId,
          category_id: selectedCategory || ''
        })
      );
    }
  }, [dispatch, isEditMode, documentId, selectedProjectId, selectedServiceTitle, selectedCategory]);

  // Reset fetch flag when documentId changes
  useEffect(() => {
    if (documentId) {
      hasFetchedTemplate.current = false;
      templateInitialized.current = false;
      hasSubmitted.current = false; // Reset submission flag when document changes
    }
  }, [documentId]);

  // Patch template data into state and fetch preview when fetched - FIXED
  useEffect(() => {
    if (
      isEditMode &&
      selectedTemplate &&
      selectedTemplate.documentId === documentId &&
      !templateInitialized.current
    ) {
      console.log('Initializing template data:', selectedTemplate);
      templateInitialized.current = true;

      setStep(2); // Ensure step 2 for edit mode
      setDocumentNameState(selectedTemplate.documentName || documentName || '');
      setCategory(selectedTemplate.category || selectedCategory || '');
      setSubCategory(selectedTemplate.subCategory || '');

      // Patch uploadedFile and fetch preview
      if (selectedTemplate.uploadedFile && selectedTemplate.uploadedFile.storedFilePath) {
        const {
          fileName = 'unknown',
          fileType = 'pdf',
          fileSize,
          originalFileName,
          storedFilePath
        } = selectedTemplate.uploadedFile;

        fetchDocumentPreview(storedFilePath).then((blobUrl) => {
          if (blobUrl) {
            const file = new File([new Blob()], fileName, { type: `application/${fileType}` });
            setUploadedFile({
              fileName,
              fileSize,
              fileType,
              originalFileName,
              storedFilePath,
              file,
              preview: blobUrl,
              type:
                fileType === 'pdf' ? 'pdf' : fileType.startsWith('image') ? 'image' : 'document',
              base64: undefined
            });
          }
        });
      } else {
        setErrorMessage('No file associated with this template.');
      }

      // Patch signatureFields
      if (selectedTemplate.inputFields) {
        setSignatureFields(
          selectedTemplate.inputFields.map((field) => ({
            id: field.id,
            type: field.type as FieldDataType,
            x: field.x,
            y: field.y,
            xPercent: field.xPercent ?? 0,
            yPercent: field.yPercent ?? 0,
            widthPercent: field.widthPercent ?? 0,
            heightPercent: field.heightPercent ?? 0,
            pageNumber: field.pageNumber,
            value: field.value ?? '',
            isRequired: field.isRequired,
            fieldLabel: field.fieldLabel || 'Unknown',
            width: field.width ?? 0,
            height: field.height ?? 0
          }))
        );

        // Patch fieldRequirements
        const requirements: Record<string, boolean> = {};
        selectedTemplate.inputFields.forEach((field) => {
          requirements[field.id] = field.isRequired;
        });
        setFieldRequirements(requirements);
      }
    }
  }, [isEditMode, selectedTemplate, documentId, documentName, selectedCategory]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (documentPreviewUrl) {
        URL.revokeObjectURL(documentPreviewUrl);
      }
    };
  }, []);

  // FIXED: Enhanced success handling - only runs when user actually submits
  useDidUpdateEffect(() => {
    if (templateSuccess && hasSubmitted.current) {
      setSuccessMessage(
        isEditMode ? 'Template updated successfully!' : 'Template created successfully!'
      );
      setStep(4); // Show success step

      if (isEditMode) {
        setTimeout(() => {
          setActiveView('templates');
          navigate('/payroll/documents/templates');
        }, 5000);
      } else {
        setTimeout(() => {
          resetToStepOne();
        }, 3000);
      }

      // Reset the submission flag after handling
      hasSubmitted.current = false;
    }
  }, [templateSuccess]);

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
    dispatch(clearTemplateError());
    dispatch(clearCategoryError());
    dispatch(clearSubcategoryError());
  };

  const handleStepBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleStepNext = () => {
    if (step < 3) {
      setStep(step + 1);
    }
  };

  // NEW: Function to handle save/submit actions from TemplateConfigStep
  const handleSaveTemplate = () => {
    hasSubmitted.current = true; // Mark that user has submitted
    // The actual save logic should be in TemplateConfigStep
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        px: isMobile ? 0.5 : 1,
        py: isMobile ? 0.5 : 1,
        width: '100%'
      }}
    >
      {/* Processing Backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={templateCreating || previewLoading || templateFetching}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" sx={{ fontSize: isMobile ? '14px' : '16px' }}>
          {templateCreating
            ? isEditMode
              ? 'Updating template...'
              : 'Creating template...'
            : previewLoading
            ? 'Loading document preview...'
            : 'Fetching template data...'}
        </Typography>
      </Backdrop>

      {/* Header */}
      <Box sx={{ mb: isMobile ? 1 : 1.5 }}>
        <Typography
          variant={isMobile ? 'subtitle1' : 'h5'}
          sx={{ fontWeight: 'bold', color: '#217346', mb: 0.5 }}
        >
          {isEditMode ? 'Edit Template' : 'Create Template'}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: isMobile ? '10px' : '12px' }}
        >
          {isEditMode
            ? `Edit template: ${documentNameState || 'Untitled'}`
            : 'Create document templates with required fields'}
        </Typography>
      </Box>

      {/* Snackbars */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ fontSize: isMobile ? '10px' : '12px' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!(templateError || categoryError || subcategoryError || errorMessage)}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ fontSize: isMobile ? '10px' : '12px' }}
          action={
            errorMessage?.includes('preview') && selectedTemplate?.uploadedFile?.storedFilePath ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => fetchDocumentPreview(selectedTemplate.uploadedFile.storedFilePath!)}
              >
                Retry
              </Button>
            ) : undefined
          }
        >
          {templateError || categoryError || subcategoryError || errorMessage}
        </Alert>
      </Snackbar>

      {/* Main Content */}
      <Box sx={{ minHeight: 'calc(100vh - 140px)' }}>
        <Fade in={true} key={step}>
          <Box>
            {/* Step 1: Upload Document */}
            {step === 1 && (
              <DocumentUploadStep
                uploadedFile={uploadedFile}
                setUploadedFile={setUploadedFile}
                setSuccessMessage={setSuccessMessage}
                onNext={handleStepNext}
              />
            )}

            {/* Step 2: Define Document Fields */}
            {step === 2 && (
              <DocumentEditingStep
                uploadedFile={uploadedFile}
                setUploadedFile={setUploadedFile}
                signatureFields={signatureFields}
                setSignatureFields={setSignatureFields}
                fieldRequirements={fieldRequirements}
                setFieldRequirements={setFieldRequirements}
                setSuccessMessage={setSuccessMessage}
                onBack={handleStepBack}
                onNext={handleStepNext}
              />
            )}

            {/* Step 3: Template Configuration */}
            {step === 3 && (
              <TemplateConfigStep
                documentNameState={documentNameState}
                setDocumentNameState={setDocumentNameState}
                category={category}
                setCategory={setCategory}
                subCategory={subCategory}
                setSubCategory={setSubCategory}
                uploadedFile={uploadedFile}
                signatureFields={signatureFields}
                fieldRequirements={fieldRequirements}
                availableCategories={availableCategories}
                isEditMode={isEditMode}
                onBack={handleStepBack}
                onSave={handleSaveTemplate} // NEW: Pass the save handler
              />
            )}

            {/* Step 4: Success */}
            {step === 4 && (
              <Box textAlign="center" sx={{ py: isMobile ? 3 : 4 }}>
                <CheckCircleIcon sx={{ fontSize: isMobile ? 48 : 64, color: '#217346', mb: 1 }} />
                <Typography
                  variant={isMobile ? 'subtitle2' : 'h6'}
                  sx={{ mb: 1, color: '#333', fontWeight: 600 }}
                >
                  Template {isEditMode ? 'Updated' : 'Created'} Successfully!
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  Your template "{documentNameState}" is ready to use.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                  {isEditMode ? 'Updated' : 'Created'}: {new Date().toLocaleDateString()} •{' '}
                  {signatureFields.length} fields •{' '}
                  {Object.values(fieldRequirements).filter(Boolean).length} required
                  {subCategory && ` • Tag: ${subCategory}`}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '9px', mt: 1, display: 'block' }}
                >
                  {isEditMode
                    ? 'Redirecting to templates view in 5 seconds...'
                    : 'Resetting form in 3 seconds...'}
                </Typography>
              </Box>
            )}
          </Box>
        </Fade>
      </Box>
    </Container>
  );
};

export default CreateDocumentPage;
